
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { PackageItem } from '@/types/package-item';

const formSchema = z.object({
  text_ar: z.string().min(1, { message: 'النص بالعربية مطلوب' }),
  text_en: z.string().min(1, { message: 'النص بالإنجليزية مطلوب' }),
});

interface PackageExclusionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item?: PackageItem | null;
  onSave: (values: z.infer<typeof formSchema>) => void;
  isSaving: boolean;
}

export function PackageExclusionDialog({ open, onOpenChange, item, onSave, isSaving }: PackageExclusionDialogProps) {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      text_ar: '',
      text_en: '',
    },
  });

  useEffect(() => {
    if (item) {
      form.reset({
        text_ar: item.text_ar,
        text_en: item.text_en,
      });
    } else {
      form.reset({
        text_ar: '',
        text_en: '',
      });
    }
  }, [item, form]);

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    onSave(values);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]" dir="rtl">
        <DialogHeader>
          <DialogTitle>
            {item ? 'تعديل البند' : 'إضافة بند جديد'}
          </DialogTitle>
          <DialogDescription>
            {item 
              ? 'قم بتعديل بيانات البند.' 
              : 'أدخل بيانات البند الجديد.'
            }
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 pt-4">
            <FormField
              control={form.control}
              name="text_ar"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>النص بالعربية</FormLabel>
                  <FormControl>
                    <Textarea placeholder="أدخل النص بالعربية..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="text_en"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>النص بالإنجليزية</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Enter text in English..." {...field} dir="ltr" className="text-left" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                إلغاء
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? 'جاري الحفظ...' : (item ? 'تحديث' : 'إضافة')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
